import {Directory, Filesystem} from "@capacitor/filesystem";
import {Preferences} from "@capacitor/preferences";

// Short confirmation sounds played after a scan. The files live in the extranet so they can be
// swapped without an app release; they are kept on the device and decoded ahead of time, so
// playing one never waits on the network.

export type ScanSoundKind = 'success' | 'error';

const KINDS: ScanSoundKind[] = ['success', 'error'];
const VERSIONS_KEY = 'scanSounds.versions';
const FETCH_TIMEOUT_MS = 10000;

type Versions = Partial<Record<ScanSoundKind, string>>;

let context: AudioContext | null = null;
let deviceLoad: Promise<void> | null = null;
let refreshInFlight: Promise<void> | null = null;
const buffers: Partial<Record<ScanSoundKind, AudioBuffer>> = {};

const apiUrl = (path: string) => process.env.REACT_APP_API_ENDPOINT + path;
const devicePath = (kind: ScanSoundKind) => `scan-sounds/${kind}.wav`;

const getContext = (): AudioContext | null => {
    if (context) {
        return context;
    }
    const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) {
        return null;
    }
    try {
        // Browsers cap the number of audio contexts a page may create; without one there is
        // simply no sound.
        context = new AudioContextClass() as AudioContext;
    } catch (e) {
        console.warn('[scanSound] audio unavailable', e);
        return null;
    }
    // iOS keeps a new context suspended until the first touch.
    document.addEventListener('touchend', () => {
        context?.resume().catch(() => undefined);
    }, {once: true});
    return context;
};

const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
};

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i += 0x8000) {
        binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + 0x8000)));
    }
    return btoa(binary);
};

const decode = (ctx: AudioContext, data: ArrayBuffer): Promise<AudioBuffer> =>
    new Promise((resolve, reject) => {
        // decodeAudioData detaches the buffer it is given, so it gets a copy.
        const pending = ctx.decodeAudioData(data.slice(0), resolve, reject) as Promise<AudioBuffer> | undefined;
        if (pending && typeof pending.catch === 'function') {
            pending.catch(() => undefined);
        }
    });

// The limit covers reading the body too - a response can arrive quickly and then stall.
const fetchWithTimeout = async <T>(url: string, read: (response: Response) => Promise<T>): Promise<T> => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
        const response = await fetch(url, {signal: controller.signal});
        if (!response.ok) {
            throw new Error(`HTTP ${response.status} for ${url}`);
        }
        return await read(response);
    } finally {
        clearTimeout(timer);
    }
};

const readVersions = async (): Promise<Versions> => {
    const {value} = await Preferences.get({key: VERSIONS_KEY});
    return value ? JSON.parse(value) : {};
};

const loadFromDevice = async (ctx: AudioContext, kind: ScanSoundKind): Promise<void> => {
    try {
        const file = await Filesystem.readFile({path: devicePath(kind), directory: Directory.Data});
        if (typeof file.data !== 'string') {
            return;
        }
        const buffer = await decode(ctx, base64ToArrayBuffer(file.data));
        // A fresher sound downloaded meanwhile wins over the stored one.
        if (!buffers[kind]) {
            buffers[kind] = buffer;
        }
    } catch (e) {
        // Nothing stored yet - the network step may still provide the sound.
    }
};

const refreshFromNetwork = async (ctx: AudioContext): Promise<void> => {
    const manifest = await fetchWithTimeout(apiUrl('api/v2/scan-sounds'), response => response.json());
    const versions = await readVersions();

    for (const kind of KINDS) {
        const version: string | undefined = manifest?.[kind]?.version;
        // A stored version whose file is missing or undecodable on the device is downloaded again.
        if (!version || (version === versions[kind] && buffers[kind])) {
            continue;
        }
        try {
            const data = await fetchWithTimeout(apiUrl(`api/v2/scan-sounds/${kind}?v=${version}`), response => response.arrayBuffer());
            // Decode before storing: a broken file is never kept or recorded as current.
            const buffer = await decode(ctx, data);
            await Filesystem.writeFile({path: devicePath(kind), directory: Directory.Data, data: arrayBufferToBase64(data), recursive: true});
            versions[kind] = version;
            await Preferences.set({key: VERSIONS_KEY, value: JSON.stringify(versions)});
            buffers[kind] = buffer;
        } catch (e) {
            console.warn('[scanSound] could not update', kind, e);
        }
    }
};

export const preloadScanSounds = (): Promise<void> => {
    const ctx = getContext();
    if (!ctx) {
        return Promise.resolve();
    }
    if (!deviceLoad) {
        deviceLoad = Promise.all(KINDS.map(kind => loadFromDevice(ctx, kind))).then(() => undefined);
    }
    if (!refreshInFlight) {
        // Every entry into a report checks for newer sounds, so a replaced file or a failed
        // earlier attempt (offline, timeout) is picked up without restarting the app.
        refreshInFlight = deviceLoad
            .then(() => refreshFromNetwork(ctx))
            .catch(e => console.warn('[scanSound] sounds not refreshed', e))
            .then(() => {
                refreshInFlight = null;
            });
    }
    return refreshInFlight;
};

export const playScanSound = (kind: ScanSoundKind): void => {
    try {
        const buffer = buffers[kind];
        if (!context || !buffer) {
            return;
        }
        if (context.state === 'suspended') {
            context.resume().catch(() => undefined);
        }
        const source = context.createBufferSource();
        source.buffer = buffer;
        source.connect(context.destination);
        source.start();
    } catch (e) {
        console.warn('[scanSound] could not play', kind, e);
    }
};
